import { useI18n } from 'vue-i18n'

/**
 * Maps every supported alert condition type to its i18n label key.
 * The `_desc` variant of each key is used for the description.
 *
 * Single source of truth — keep this in sync with the backend's
 * supported condition types. Adding a new type requires:
 *   1. Adding it here
 *   2. Adding `alerts.condition_<type>` + `alerts.condition_<type>_desc`
 *      to BOTH `src/i18n/locales/en.json` and `src/i18n/locales/nl.json`
 *   3. Wiring it into AlertConditionPicker.vue categories (if user-pickable)
 */
const TYPE_KEYS = {
  threshold: 'alerts.condition_threshold',
  relative_change: 'alerts.condition_relative_change',
  count_in_period: 'alerts.condition_count_in_period',
  negative_quarters: 'alerts.condition_negative_quarters',
  trend: 'alerts.condition_trend',
  threshold_duration: 'alerts.condition_threshold_duration',
  spread: 'alerts.condition_spread',
  moving_average: 'alerts.condition_moving_average',
  crosses_ma: 'alerts.condition_crosses_ma',
  forecast_deviation: 'alerts.condition_forecast_deviation',
  prediction_confidence: 'alerts.condition_prediction_confidence',
  record: 'alerts.condition_record',
  compound: 'alerts.condition_compound',
  cross_market_spread: 'alerts.condition_cross_market_spread',
  state_change: 'alerts.condition_state_change',
  data_missing: 'alerts.condition_data_missing'
}

/**
 * Retired types that are no longer offered (not in `knownTypes`) but may still
 * appear on stored evaluation-history rows. Labels resolve; descriptions don't.
 * `streak` was folded into `threshold_duration` (the backend normalises it).
 */
const LEGACY_TYPE_KEYS = {
  streak: 'alerts.condition_streak'
}

/**
 * Single source of truth for alert condition-type display labels and descriptions.
 *
 * Pass `{ i18n }` to inject a custom vue-i18n instance for tests;
 * defaults to `useI18n()` in production usage (must be called within a setup()).
 *
 * @param {{ i18n?: import('vue-i18n').I18n }} [options]
 * @returns {{
 *   label: (type: string) => string,   // also resolves retired types
 *   description: (type: string) => string,
 *   knownTypes: string[]              // offered types only
 * }}
 */
export function useAlertConditionMeta ({ i18n } = {}) {
  const tFn = i18n ? (key) => i18n.global.t(key) : useI18n().t

  function label (type) {
    const key = TYPE_KEYS[type] || LEGACY_TYPE_KEYS[type]
    if (!key) return type
    const translated = tFn(key)
    return translated === key ? type : translated
  }

  function description (type) {
    const key = TYPE_KEYS[type]
    if (!key) return ''
    const descKey = `${key}_desc`
    const translated = tFn(descKey)
    return translated === descKey ? '' : translated
  }

  return { label, description, knownTypes: Object.keys(TYPE_KEYS) }
}
